<template>
  <SearchUserInput
    :key="driverInputKey"
    class="min-w-56"
    variant="inline"
    clearable
    :allowed-roles="driverRoles"
    :selected-user="allocation.driver ?? undefined"
    :placeholder="t('noDriverSelected')"
    :disabled="isUpdatingAllocation"
    @update-user="handleDriverUpdate"
  />
</template>

<script setup lang="ts">
import SearchUserInput from '@/components/booking-forms/customer/SearchUserInput.vue';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { UserRole } from '@/entities/auth/user-role.enum';
import type { User } from '@/entities/auth/user.entity';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { Alert } from '@/services/alert.service';
import { useQueryClient } from '@tanstack/vue-query';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  allocation: Allocation;
  bookingId: string;
}>();

const { t } = useI18n();
const queryClient = useQueryClient();
const { mutateAsync: updateAllocation, isPending: isUpdatingAllocation } = useUpdateAllocation();

const driverRoles = [UserRole.ADMIN, UserRole.HUB_MANAGER, UserRole.HUB_AGENT, UserRole.OPERATOR];
const driverInputKey = ref(0);

const handleDriverUpdate = async (user: User | undefined) => {
  const newDriverId = user?.id ?? null;
  const currentDriverId = props.allocation.driver?.id ?? null;
  if (newDriverId === currentDriverId) return;

  if (!user && currentDriverId) {
    const { isConfirmed } = await Alert.fire({
      text: t('removeDriverWarningText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('remove'),
      cancelButtonText: t('cancel'),
    });
    if (!isConfirmed) {
      driverInputKey.value++;
      return;
    }
  }

  await updateAllocation({
    id: props.allocation.id,
    allocation: { driverId: newDriverId },
  });
  await queryClient.invalidateQueries({ queryKey: ['bookings', props.bookingId] });
};
</script>

<i18n lang="json">
{
  "en": {
    "noDriverSelected": "No driver selected",
    "remove": "Remove",
    "cancel": "Cancel",
    "removeDriverWarningText": "Do you really want to remove the driver?"
  },
  "de": {
    "noDriverSelected": "Kein Fahrer ausgewählt",
    "remove": "Entfernen",
    "cancel": "Abbrechen",
    "removeDriverWarningText": "Möchtest du den Fahrer wirklich entfernen?"
  },
  "es": {
    "noDriverSelected": "Ningún conductor seleccionado",
    "remove": "Eliminar",
    "cancel": "Cancelar",
    "removeDriverWarningText": "¿Seguro que desea eliminar al conductor?"
  }
}
</i18n>
